import {defineStore} from 'pinia'
import {computed, ref} from 'vue'
import {Capacitor} from '@capacitor/core'
import {
  DatabaseItemLayoutType,
} from '@toolify/client/src/modules/databases/enum/DatabaseItemLayoutType'
import {
  IDatabaseItemLayoutByViewId,
} from '@toolify/client/src/modules/databases/types/IDatabaseItemLayoutByViewId'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {useLocalPreferenceStore} from '@toolify/client/src/stores/LocalPreferenceStore/useLocalPreferenceStore'
import {LocalPreferenceType} from '@toolify/client/src/stores/LocalPreferenceStore/enum/LocalPreferenceType'
import {useResponsive} from '@toolify/client/src/composables/useResponsive'
import {setDatabaseItemLayoutForView} from '@toolify/client/src/modules/databases/functions/setDatabaseItemLayoutForView'
import {IDatabaseItemRouteSource} from '@toolify/client/src/modules/databases/types/IDatabaseItemRouteSource'

export const DEFAULT_DATABASE_ITEM_LAYOUT = DatabaseItemLayoutType.Sidebar

const DATABASE_ITEM_LAYOUT_TYPES = new Set<string>(Object.values(DatabaseItemLayoutType))

function parseLayoutByViewId(value: string | undefined): IDatabaseItemLayoutByViewId {
  if(!value) {
    return {}
  }
  try {
    const parsed = JSON.parse(value)
    if(!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }
    return Object.fromEntries(Object.entries(parsed).filter(([, layout]) => {
      return DATABASE_ITEM_LAYOUT_TYPES.has(layout as string)
    })) as IDatabaseItemLayoutByViewId
  } catch {
    return {}
  }
}

export const useDatabaseItemLayoutStore = defineStore('databaseItemLayout', () => {
  const authStore = useAuthStore()
  const localPreferenceStore = useLocalPreferenceStore()
  const {isSmallerThanLarge} = useResponsive()

  const sessionLayouts = ref<{userId: string, layoutByViewId: IDatabaseItemLayoutByViewId} | null>(null)
  const relatedItemLayout = ref<DatabaseItemLayoutType>(DEFAULT_DATABASE_ITEM_LAYOUT)

  const layoutByViewId = computed<IDatabaseItemLayoutByViewId>(() => {
    const userId = authStore.currentUser?.id
    if(!userId) {
      return {}
    }
    if(sessionLayouts.value?.userId === userId) {
      return sessionLayouts.value.layoutByViewId
    }
    return parseLayoutByViewId(localPreferenceStore.get(LocalPreferenceType.DatabaseItemLayoutByViewId, userId))
  })

  function getLayout(viewId: string | null | undefined): DatabaseItemLayoutType {
    if(!viewId) {
      return DEFAULT_DATABASE_ITEM_LAYOUT
    }
    return layoutByViewId.value[viewId] ?? DEFAULT_DATABASE_ITEM_LAYOUT
  }

  function setLayout(viewId: string | null | undefined, layout: DatabaseItemLayoutType) {
    const userId = authStore.currentUser?.id
    if(!viewId || !userId || getLayout(viewId) === layout) {
      return
    }
    const nextLayoutByViewId = layout === DEFAULT_DATABASE_ITEM_LAYOUT
      ? Object.fromEntries(Object.entries(layoutByViewId.value).filter(([entryViewId]) => entryViewId !== viewId))
      : setDatabaseItemLayoutForView(layoutByViewId.value, viewId, layout)
    if(Capacitor.isNativePlatform() || isSmallerThanLarge.value) {
      sessionLayouts.value = {
        userId,
        layoutByViewId: nextLayoutByViewId,
      }
      return
    }
    void localPreferenceStore.set(LocalPreferenceType.DatabaseItemLayoutByViewId, JSON.stringify(nextLayoutByViewId), userId)
  }

  function getSourceLayout({viewId, isRelated}: Pick<IDatabaseItemRouteSource, 'viewId' | 'isRelated'>): DatabaseItemLayoutType {
    return isRelated ? relatedItemLayout.value : getLayout(viewId)
  }

  function setRelatedItemLayout(layout: DatabaseItemLayoutType) {
    relatedItemLayout.value = layout
  }

  return {
    layoutByViewId,
    relatedItemLayout,
    getLayout,
    setLayout,
    getSourceLayout,
    setRelatedItemLayout,
  }
})
